'use client';

import { forwardRef, type HTMLAttributes } from 'react';
import { Alert, IconButton, Portal } from '@mui/material';
import { styled, useThemeProps, type CSSObject } from '@mui/material/styles';

import { Close } from './icons';

import { type AnchorOrigin, useSnackbarNotification } from './SnackbarContext';

export interface SnackbarListProps extends HTMLAttributes<HTMLDivElement> {
    anchorOrigin: AnchorOrigin;
    ariaLabel?: string;
}

interface SnackbarListOwnerState {
    anchorOrigin: AnchorOrigin;
}

const positionStyles: Record<AnchorOrigin, CSSObject> = {
    topLeft: { top: 16, left: 16 },
    topCenter: { top: 16, left: '50%', transform: 'translateX(-50%)' },
    topRight: { top: 16, right: 16 },
    bottomLeft: { bottom: 16, left: 16 },
    bottomCenter: { bottom: 16, left: '50%', transform: 'translateX(-50%)' },
    bottomRight: { bottom: 16, right: 16 },
};

const severityMap = {
    default: undefined,
    success: 'success' as const,
    error: 'error' as const,
    warning: 'warning' as const,
    info: 'info' as const,
};

const SnackbarListRoot = styled('div', {
    name: 'MuiSnackbarList',
    slot: 'Root',
})<{ ownerState: SnackbarListOwnerState }>(({ theme, ownerState }) => ({
    position: 'fixed',
    display: 'flex',
    flexDirection: 'column-reverse',
    gap: theme.spacing(1),
    zIndex: theme.zIndex.snackbar,
    ...positionStyles[ownerState.anchorOrigin],
}));

export const SnackbarList = forwardRef<HTMLDivElement, SnackbarListProps>(
    function SnackbarList(inProps, ref) {
        const props = useThemeProps({ props: inProps, name: 'MuiSnackbarList' });
        const { anchorOrigin, ariaLabel, className, ...other } = props;
        const { notifications, removeNotification } = useSnackbarNotification();
        const items = notifications[anchorOrigin];

        if (items.length === 0) {
            return null;
        }

        const ownerState: SnackbarListOwnerState = { anchorOrigin };

        return (
            <Portal>
                <SnackbarListRoot
                    ref={ref}
                    ownerState={ownerState}
                    role="region"
                    aria-label={ariaLabel ?? 'Notifications'}
                    className={className}
                    {...other}>
                    {items.map((notification) => {
                        const severity = severityMap[notification.variant ?? 'default'];
                        return (
                            <Alert
                                key={notification.id}
                                severity={severity}
                                variant="filled"
                                action={
                                    <>
                                        {notification.action}
                                        <IconButton
                                            size="small"
                                            aria-label="close"
                                            color="inherit"
                                            sx={{ fontSize: '1.125rem' }}
                                            onClick={() => {
                                                removeNotification(anchorOrigin, notification.id);
                                            }}>
                                            <Close fontSize="inherit" />
                                        </IconButton>
                                    </>
                                }>
                                {notification.message}
                            </Alert>
                        );
                    })}
                </SnackbarListRoot>
            </Portal>
        );
    }
);
